import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText, Send, ChevronDown, ChevronRight, CheckCircle2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import FormRenderer from "@/components/forms/FormRenderer";
import { validateForm } from "@/lib/formEngine";
import { api, API, getToken, formatApiError } from "@/lib/api";

function renderValue(v) {
  if (v && typeof v === "object" && v.att_id)
    return <a href={`${API}/uploads/${v.att_id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer" className="text-[#0B7E8C] underline">{v.name || "file"}</a>;
  if (typeof v === "string" && v.startsWith("data:image"))
    return <img src={v} alt="signature" className="inline-block h-7 border border-[#E5E7EB] rounded bg-white align-middle" />;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

// Renders admin-enabled ("placed") dynamic forms inside a host record.
// Pass `forms` (from GET /forms/placed?module=) to avoid a duplicate fetch, or omit to self-fetch.
export default function EmbeddedForms({ module, entityId, entityLabel, forms: formsProp, extraContext, readOnly = false }) {
  const { user } = useAuth();
  const role = user?.role_code;
  const [forms, setForms] = useState(formsProp || []);
  const [subs, setSubs] = useState({});       // formId -> submissions[]
  const [open, setOpen] = useState({});        // formId -> bool (expanded history)
  const [fill, setFill] = useState(null);      // { form } currently being filled
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (formsProp) setForms(formsProp); }, [formsProp]);

  const loadSubs = useCallback(async (formId) => {
    try {
      const r = await api.get(`/forms/${formId}/submissions`, { params: { entity_id: entityId } });
      setSubs((p) => ({ ...p, [formId]: r.data.items }));
    } catch { /* ignore */ }
  }, [entityId]);

  useEffect(() => {
    if (!formsProp) {
      api.get("/forms/placed", { params: { module } }).then((r) => setForms(r.data.items)).catch(() => {});
    }
  }, [module, formsProp]);

  useEffect(() => { forms.forEach((f) => loadSubs(f.id)); }, [forms, loadSubs]);

  function startFill(form) {
    setFill({ form });
    setValues({});
    setErrors({});
  }

  async function submit() {
    const errs = validateForm(fill.form.definition, values, role);
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      await api.post(`/forms/${fill.form.id}/submit`, {
        data: values,
        context: { module, entity_id: entityId, entity_label: entityLabel, ...(extraContext || {}) },
      });
      toast.success("Form submitted");
      await loadSubs(fill.form.id);
      setFill(null);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  }

  if (!forms.length) {
    return <div className="text-sm text-[#6B7280] py-8 text-center" data-testid="embedded-forms-empty">No forms are enabled for this module yet.</div>;
  }

  return (
    <div className="space-y-3" data-testid="embedded-forms">
      {forms.map((f) => {
        const list = subs[f.id] || [];
        return (
          <div key={f.id} className="hah-card p-4" data-testid={`embedded-form-${f.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
                <div className="min-w-0">
                  <div className="font-medium text-[#1F2937]">{f.name} <span className="text-[11px] text-[#94A3B8] font-mono">v{f.published_version}</span></div>
                  {f.description && <div className="text-xs text-[#6B7280] mt-0.5">{f.description}</div>}
                </div>
              </div>
              {!readOnly && (
                <Button size="sm" onClick={() => startFill(f)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white shrink-0" data-testid={`fill-form-${f.key}`}>
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Fill form
                </Button>
              )}
            </div>

            <button onClick={() => setOpen((p) => ({ ...p, [f.id]: !p[f.id] }))} className="mt-3 flex items-center gap-1.5 text-xs text-[#475569] hover:text-[#0B7E8C]" data-testid={`toggle-subs-${f.key}`}>
              {open[f.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {list.length} submission{list.length === 1 ? "" : "s"} for this record
            </button>
            {open[f.id] && (
              <div className="mt-2 space-y-2 border-t border-[#E5E7EB] pt-2">
                {list.length === 0 && <div className="text-xs text-[#94A3B8] italic">No submissions yet.</div>}
                {list.map((s) => (
                  <div key={s.id} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg p-3" data-testid={`submission-${s.id}`}>
                    <div className="flex items-center gap-2 text-xs text-[#475569] mb-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
                      <span className="font-medium">{s.submitted_by_name || "User"}</span>
                      <span className="text-[#94A3B8]">· v{s.version}</span>
                      <span className="ml-auto flex items-center gap-1 text-[#94A3B8]"><Clock className="w-3 h-3" /> {(s.submitted_at || "").slice(0, 19).replace("T", " ")}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {Object.entries(s.data || {}).map(([k, v]) => (
                        <div key={k} className="truncate"><span className="text-[#94A3B8]">{k}:</span> <span className="text-[#1F2937]">{renderValue(v)}</span></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <Sheet open={!!fill} onOpenChange={(o) => { if (!o) setFill(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-[680px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>{fill?.form?.name}</SheetTitle>
            <SheetDescription>{entityLabel ? `Record: ${entityLabel}` : "Fill and submit this form."}</SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {fill && <FormRenderer definition={fill.form.definition} role={role} values={values} onChange={setValues} errors={errors} />}
          </div>
          <div className="hah-sticky-bar px-6 py-3 flex items-center justify-end gap-3 sticky bottom-0 bg-white border-t border-[#E5E7EB]">
            <Button variant="outline" onClick={() => setFill(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="submit-embedded-form">
              <Send className="w-4 h-4 mr-1.5" /> {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
